/**
 * Campo de senha com o olho de mostrar/esconder — o padrão da casa, o mesmo
 * do login.
 *
 * Existe como componente e não copiado em cada tela porque campo de senha
 * aparece em seis lugares: trocar a própria, redefinir pelo link, senha
 * provisória no cadastro e na redefinição pelo admin. Copiado, um deles fica
 * sem o olho — foi o que aconteceu antes de isto existir.
 *
 * Começa escondido. Quem quer conferir clica; quem só digita não expõe a
 * senha para quem estiver ao lado.
 */
import { Eye, EyeOff } from 'lucide-react';
import { useId, useState, type InputHTMLAttributes, type ReactNode } from 'react';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label: string;
  /** Conteúdo extra abaixo do campo — dica, requisitos da senha. */
  abaixo?: ReactNode;
  /**
   * Senha provisória que o administrador vai copiar para enviar: nasce
   * visível, porque escondê-la só atrapalha quem precisa lê-la.
   */
  iniciarVisivel?: boolean;
};

export function CampoSenha({ label, abaixo, iniciarVisivel = false, ...input }: Props) {
  const [visivel, setVisivel] = useState(iniciarVisivel);
  const id = useId();

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-xs font-medium text-muted-foreground">
        {label}
      </label>
      <div className="relative">
        <input
          {...input}
          id={id}
          type={visivel ? 'text' : 'password'}
          // pr-9 reserva o espaço do botão: sem isso o texto passa por baixo do ícone.
          className={`input pr-9 ${input.className ?? ''}`}
        />
        <button
          type="button"
          onClick={() => setVisivel((v) => !v)}
          // tabIndex -1: quem navega por teclado vai do campo direto para o
          // próximo, sem esbarrar num botão que só ajuda quem usa o mouse.
          tabIndex={-1}
          aria-label={visivel ? 'Esconder senha' : 'Mostrar senha'}
          title={visivel ? 'Esconder senha' : 'Mostrar senha'}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
        >
          {visivel ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {abaixo}
    </div>
  );
}
